import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { UserContact } from '../../../models/user-contact';
import { ContactPickerService } from '../../../webclient/contact-picker/contact-picker.service';
import { MessageFilterCriteria, createEmptyMessageFilterCriteria } from './message-filter-criteria';
import * as _ from 'lodash';

/**
 * Drop-down panel of the in-chat message filter: sender (contact picker) and a date range.
 * Edits are kept as a draft until Apply; Clear resets and applies the empty filter.
 */
@Component({
  selector: 'tn-message-filter-panel',
  templateUrl: './message-filter-panel.component.html',
  styleUrls: ['./message-filter-panel.component.scss']
})
export class MessageFilterPanelComponent implements OnInit {

  // the applied criteria the draft starts from
  @Input() criteria: MessageFilterCriteria = null;
  // contacts the sender picker offers
  @Input() selectableContacts: UserContact[] = [];

  @Output() onApply = new EventEmitter<MessageFilterCriteria>();
  @Output() onClose = new EventEmitter<void>();

  draftUsers: UserContact[] = [];
  draftDateFrom: Date = null;
  draftDateTo: Date = null;
  isInvalidDateRange = false;

  dateInputConfig: Partial<BsDatepickerConfig>;

  constructor(
    private _contactPickerService: ContactPickerService
  ) { }

  ngOnInit() {
    this.dateInputConfig = Object.assign({}, {
      containerClass: 'theme-default',
      dateInputFormat: 'YYYY-MM-DD',
      // Keep the calendar inside the viewport: the panel sits at the right edge of the
      // search bar, so a picker opened from "To" otherwise overflows on narrow windows.
      adaptivePosition: true
    });

    const criteria = this.criteria || createEmptyMessageFilterCriteria();
    this.draftUsers = _.cloneDeep(criteria.users || []);
    this.draftDateFrom = criteria.dateFrom != null ? new Date(criteria.dateFrom * 1000) : null;
    this.draftDateTo = criteria.dateTo != null ? new Date(criteria.dateTo * 1000) : null;
  }

  openSenderPicker(): void {
    this._contactPickerService.openContactPicker(
      'WEBCLIENT.CHAT.MESSAGE_FILTER.SENDER',
      false,
      false,
      true,
      (contacts: UserContact[]) => {
        this.draftUsers = _.cloneDeep(contacts || []);
      },
      true,
      null,
      this.selectableContacts,
      false,
      this.draftUsers,
      true, // filter mode
      true // allow select all
    );
  }

  getSenderNames(): string {
    return _.map(this.draftUsers, 'name').join(', ');
  }

  onDateChanged(): void {
    this.isInvalidDateRange = false;
  }

  apply(): void {
    const dateFrom = this.draftDateFrom ? this.getStartOfLocalDaySecond(this.draftDateFrom) : null;
    const dateTo = this.draftDateTo ? this.getEndOfLocalDaySecond(this.draftDateTo) : null;
    if (dateFrom != null && dateTo != null && dateFrom > dateTo) {
      this.isInvalidDateRange = true;
      return;
    }

    this.onApply.emit({
      users: _.cloneDeep(this.draftUsers),
      dateFrom: dateFrom,
      dateTo: dateTo
    });
  }

  clear(): void {
    this.draftUsers = [];
    this.draftDateFrom = null;
    this.draftDateTo = null;
    this.isInvalidDateRange = false;
    this.onApply.emit(createEmptyMessageFilterCriteria());
  }

  close(): void {
    this.onClose.emit();
  }

  // Do not mutate the picker's Date objects, they are bound to the inputs
  getStartOfLocalDaySecond(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).getTime() / 1000;
  }

  getEndOfLocalDaySecond(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime() / 1000;
  }
}
